import React from 'react';
import PropTypes from 'prop-types';
import { TextField, Stack, Typography, Controller, useFormContext } from '@metarouter-private/mr-mui';

import { useConsentCategorySelectEnabled } from '../shared/consent-category/GatedConsentCategorySelect';
import { useConsentModeEnabled } from 'app/components/consent-mode-select/GatedConsentModeSelect';
import { GatedConsentFields } from '../shared';

export default function PipelineForm({
  pipelineId,
  pipelineVariables,
  currentConsentCategoryId,
  currentConsentMode,
  isExpanded,
}) {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const { consentCategorySelectEnabled } = useConsentCategorySelectEnabled();
  const { consentModeEnabled } = useConsentModeEnabled();
  const hasPipelineVariables = pipelineVariables.length > 0;
  const hasFormFields = consentCategorySelectEnabled || hasPipelineVariables;
  // Only render the consent column when consent content will actually show
  // (either consent gate on), so a variables-only form doesn't leave an empty half.
  const showConsent = consentCategorySelectEnabled || consentModeEnabled;

  if (!isExpanded || !hasFormFields) {
    return null;
  }

  return (
    <Stack direction="row" spacing={3} p={2} pt={1.5} alignItems="flex-start">
      {showConsent && (
        <Stack spacing={1} flex={1}>
          <Typography variant="caption" color="text.secondary">
            Consent
          </Typography>
          <GatedConsentFields
            pipelineId={pipelineId}
            defaultConsentCategoryId={currentConsentCategoryId}
            defaultConsentMode={currentConsentMode}
          />
        </Stack>
      )}

      {hasPipelineVariables && (
        <Stack spacing={1} flex={1}>
          {consentCategorySelectEnabled && (
            <Typography variant="caption" color="text.secondary">
              Pipeline Variables
            </Typography>
          )}
          {pipelineVariables.map((variable, index) => {
            const fieldName = `${pipelineId}.${variable.name}`;
            const fieldError = errors?.[pipelineId]?.[variable.name];

            return (
              <Controller
                key={`${pipelineId}-${variable.name}-${index}`}
                name={fieldName}
                control={control}
                defaultValue={variable.currentValue || ''}
                // Validation is owned by the form resolver (see confirmUpdateFormModel),
                // not per-field rules — a resolver supersedes field rules anyway, and
                // field rules only validate mounted rows.
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={variable.name}
                    fullWidth
                    variant="outlined"
                    size="small"
                    error={!!fieldError}
                    helperText={fieldError?.message}
                  />
                )}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}

PipelineForm.propTypes = {
  pipelineId: PropTypes.string.isRequired,
  pipelineVariables: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      currentValue: PropTypes.string,
    })
  ).isRequired,
  currentConsentCategoryId: PropTypes.string,
  currentConsentMode: PropTypes.string,
  isExpanded: PropTypes.bool.isRequired,
};

PipelineForm.defaultProps = {
  currentConsentCategoryId: '',
  currentConsentMode: null,
};
